import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Image as ImageIcon, Video as VideoIcon, Smile, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import EnhancedPostCard from '@/components/post/EnhancedPostCard';
import StoryViewer from '@/components/stories/StoryViewer';
import CreateStoryModal from '@/components/stories/CreateStoryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POSTS_PER_PAGE = 10;

// Circular Story Avatar Component with Sun-themed Saffron Gradient
const StoryAvatar = ({ image, name, hasNew = true, isCreate = false, onClick, hasStories = false }) => {
  return (
    <div 
      className="flex flex-col items-center flex-shrink-0 cursor-pointer group"
      onClick={onClick}
      data-testid={isCreate ? 'create-story-btn' : `story-avatar-${name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {/* Avatar Container with Gradient Border */}
      <div className="relative">
        {/* Outer Glow Effect - Soft sunrise tones */}
        {hasNew && !isCreate && (
          <div 
            className="absolute inset-0 rounded-full blur-md opacity-40 group-hover:opacity-60 transition-opacity"
            style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FF9933 50%, #FF6347 100%)',
              transform: 'scale(1.15)'
            }}
          />
        )}
        
        {/* Gradient Border Ring */}
        <div 
          className={`relative rounded-full p-[3px] ${isCreate ? '' : 'group-hover:scale-105 transition-transform'}`}
          style={{
            background: isCreate 
              ? hasStories
                ? 'linear-gradient(135deg, #FFD700 0%, #FF9933 30%, #FF6347 70%, #FF9933 100%)'
                : 'linear-gradient(135deg, #FF9933 0%, #FFB347 50%, #FF9933 100%)'
              : hasNew 
                ? 'linear-gradient(135deg, #FFD700 0%, #FF9933 30%, #FF6347 70%, #FF9933 100%)'
                : 'linear-gradient(135deg, #E5E5E5 0%, #D4D4D4 100%)'
          }}
        >
          {/* Inner White Ring */}
          <div className="rounded-full p-[2px] bg-white">
            {isCreate ? (
              /* Create Darshan Special Design */
              <div className="relative">
                <img 
                  src={image} 
                  alt={name} 
                  className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full object-cover"
                />
                {/* Plus Icon Overlay - Shows for both states but different style */}
                <div 
                  className="absolute bottom-0 right-0 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white"
                  style={{
                    background: hasStories 
                      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' // Green when has stories
                      : 'linear-gradient(135deg, #FF9933 0%, #FF6600 100%)', // Orange when no stories
                    boxShadow: '0 2px 8px rgba(255, 153, 51, 0.4)'
                  }}
                >
                  <Plus className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
              </div>
            ) : (
              /* User Avatar */
              <img 
                src={image} 
                alt={name} 
                className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full object-cover"
              />
            )}
          </div>
        </div>
        
        {/* Live/New Indicator Dot */}
        {hasNew && !isCreate && (
          <div 
            className="absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white"
            style={{
              background: 'linear-gradient(135deg, #FF9933 0%, #FF6600 100%)'
            }}
          />
        )}
      </div>
      
      {/* Name */}
      <span 
        className="text-[11px] sm:text-xs mt-2 font-medium text-gray-700 truncate w-16 sm:w-[72px] text-center"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        {isCreate ? (hasStories ? '+ Add More' : 'Your Darshan') : name}
      </span>
    </div>
  );
};

export default function FeedPage({ user }) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPosts, setTotalPosts] = useState(0);
  const [userStories, setUserStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const [storyViewerIndex, setStoryViewerIndex] = useState(0);
  const [showCreateStory, setShowCreateStory] = useState(false);
  
  // Refs for intersection observer and state tracking
  const isLoadingRef = useRef(false);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const observerRef = useRef(null);

  // Fetch posts with pagination
  const fetchPosts = useCallback(async (pageNum, isInitial = false) => {
    // Prevent duplicate calls
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/posts?page=${pageNum}&limit=${POSTS_PER_PAGE}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      const { posts: newPosts, has_more, total } = response.data;
      
      if (isInitial) {
        setPosts(newPosts);
      } else {
        // Append new posts to existing (avoid duplicates)
        setPosts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.id));
          return [...prev, ...uniqueNewPosts];
        });
      }
      
      setHasMore(has_more);
      hasMoreRef.current = has_more;
      setTotalPosts(total);
      setPage(pageNum);
      pageRef.current = pageNum;
    } catch (error) {
      console.error('Failed to load posts:', error);
      if (isInitial) {
        toast.error('Failed to load posts');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPosts(1, true);
    fetchStories();
  }, [fetchPosts]);

  // Intersection Observer for infinite scroll - uses refs to avoid stale closure
  // Callback ref for setting up the intersection observer
  const loadMoreRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    if (node) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasMoreRef.current && !isLoadingRef.current) {
            fetchPosts(pageRef.current + 1, false);
          }
        },
        {
          root: null,
          rootMargin: '100px',
          threshold: 0.1
        }
      );
      observerRef.current.observe(node);
    }
  }, [fetchPosts]);

  const fetchStories = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      if (!token) {
        setStoriesLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/stories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStories(response.data);
    } catch (error) {
      console.error('Failed to load stories:', error);
    } finally {
      setStoriesLoading(false);
    }
  };

  const handleStoryClick = (index) => {
    // Adjust index since we have "Your Darshan" as first item
    const actualIndex = index;
    setStoryViewerIndex(actualIndex);
    setShowStoryViewer(true);
  };

  const handleCreateStoryClick = () => {
    // If user has stories, show viewer first, otherwise show create modal
    const currentUserStories = userStories.find(us => us.user_id === user?.id);
    if (currentUserStories && currentUserStories.stories.length > 0) {
      setStoryViewerIndex(0);
      setShowStoryViewer(true);
    } else {
      setShowCreateStory(true);
    }
  };

  const handleStoryCreated = () => {
    fetchStories();
  };

  const handleStoryView = async (storyId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/stories/${storyId}/view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silent fail for view tracking
    }
  };

  const handleStoryDeleted = (storyId) => {
    // Remove deleted story from local state
    setUserStories(prev => 
      prev.map(us => ({
        ...us,
        stories: us.stories.filter(s => s.id !== storyId)
      })).filter(us => us.stories.length > 0)
    );
  };

  const handleAddAnother = () => {
    setShowCreateStory(true);
  };

  const handleSaveToHighlight = async (story) => {
    // For now, show a toast - could open highlight creation modal
    toast.success('Opening highlight creator...');
    // TODO: Open highlight creation modal with this story pre-selected
  };

  // Check if current user has stories
  const currentUserHasStories = userStories.some(us => us.user_id === user?.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-2 sm:px-4 py-2 sm:py-4 pb-24 md:pb-4">
      {/* Darshan Section - Circular Avatars with Saffron Gradient */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-3 overflow-hidden">
        {/* Section Title */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Darshan</h3>
          <button 
            className="text-xs text-[#FF9933] font-medium hover:underline"
            onClick={() => {
              if (userStories.length > 0) {
                setStoryViewerIndex(0);
                setShowStoryViewer(true);
              }
            }}
          >
            See All
          </button>
        </div>
        
        {/* Scrollable Story Bar */}
        <div 
          className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {/* Create Darshan / Your Darshan */}
          <StoryAvatar 
            isCreate={true}
            image={user?.profile_photo_url || user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=FF9933&color=fff`}
            name="Your Darshan"
            hasStories={currentUserHasStories}
            onClick={handleCreateStoryClick}
          />
          
          {/* Loading State */}
          {storiesLoading && (
            <div className="flex gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-center flex-shrink-0 animate-pulse">
                  <div className="w-[72px] h-[72px] rounded-full bg-gray-200" />
                  <div className="w-12 h-3 mt-2 rounded bg-gray-200" />
                </div>
              ))}
            </div>
          )}
          
          {/* Story Avatars from API */}
          {!storiesLoading && userStories
            .filter(us => us.user_id !== user?.id) // Filter out current user
            .map((userStory, index) => (
              <StoryAvatar
                key={userStory.user_id}
                image={userStory.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userStory.user_name || 'User')}&background=FF9933&color=fff`}
                name={userStory.user_name}
                hasNew={userStory.has_unseen}
                onClick={() => handleStoryClick(
                  // Find actual index including current user's stories if they exist
                  userStories.findIndex(us => us.user_id === userStory.user_id)
                )}
              />
            ))}
          
          {/* Empty State */}
          {!storiesLoading && userStories.filter(us => us.user_id !== user?.id).length === 0 && (
            <div className="flex items-center justify-center flex-1 min-w-[200px] py-4">
              <p className="text-sm text-gray-400">No darshan yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Post Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 mb-3">
        <div className="flex gap-2 sm:gap-3">
          <img 
            src={user?.profile_photo_url || user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=FF9933&color=fff`} 
            alt={user?.name} 
            className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover flex-shrink-0 ring-2 ring-orange-100" 
          />
          <button
            onClick={() => navigate('/create')}
            className="flex-1 min-w-0 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-full text-left text-gray-500 transition-colors text-sm truncate"
          >
            What's on your mind, {user?.name?.split(' ')[0]}?
          </button>
        </div>
        <div className="flex items-center justify-around mt-3 pt-3 border-t border-gray-100">
          <button 
            onClick={() => navigate('/create')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <VideoIcon className="w-5 h-5 text-red-500" />
            <span className="text-xs sm:text-sm font-medium text-gray-600">Video</span>
          </button>
          <button 
            onClick={() => navigate('/create')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <ImageIcon className="w-5 h-5 text-green-500" />
            <span className="text-xs sm:text-sm font-medium text-gray-600">Photo</span>
          </button>
          <button 
            onClick={() => navigate('/create')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <Smile className="w-5 h-5 text-yellow-500" />
            <span className="text-xs sm:text-sm font-medium text-gray-600">Feeling</span>
          </button>
        </div>
      </div>

      {/* Posts Feed */}
      <div className="space-y-3">
        {posts.map((post) => (
          <EnhancedPostCard
            key={post.id}
            post={post}
            currentUserId={user?.id}
            onPostUpdate={() => fetchPosts(1, true)}
            onPostDelete={(postId) => setPosts(prev => prev.filter(p => p.id !== postId))}
          />
        ))}
      </div>

      {/* Loading More Indicator */}
      {loadingMore && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-6 h-6 text-[#FF9933] animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Loading more posts...</span>
        </div>
      )}

      {/* Intersection Observer Target */}
      {hasMore && !loadingMore && (
        <div 
          ref={loadMoreRef} 
          className="h-10 flex items-center justify-center"
          data-testid="load-more-trigger"
        >
          <span className="text-xs text-gray-400">Scroll for more</span>
        </div>
      )}

      {/* End of Feed */}
      {!hasMore && posts.length > 0 && (
        <div className="text-center py-8 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="text-3xl text-[#FF9933] mb-2">🙏</div>
          <p className="text-sm text-gray-500">You've seen all {totalPosts} posts</p>
          <p className="text-xs text-gray-400 mt-1">Take a moment to reflect</p>
        </div>
      )}

      {posts.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="text-4xl mb-3">🙏</div>
          <p className="text-slate-600">No posts yet. Share your first thought!</p>
        </div>
      )}

      {/* Darshan Viewer Modal */}
      {showStoryViewer && userStories.length > 0 && (
        <StoryViewer
          userStories={userStories}
          initialUserIndex={storyViewerIndex}
          currentUserId={user?.id}
          onClose={() => setShowStoryViewer(false)}
          onStoryView={handleStoryView}
          onStoryDeleted={handleStoryDeleted}
          onAddAnother={handleAddAnother}
          onSaveToHighlight={handleSaveToHighlight}
        />
      )}

      {/* Create Darshan Modal */}
      {showCreateStory && (
        <CreateStoryModal
          user={user}
          onClose={() => setShowCreateStory(false)}
          onStoryCreated={handleStoryCreated}
        />
      )}

      {/* Hide scrollbar styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
